import { Config } from '../config';

export type PublicQuoteItem = {
    _id: string;
    title: string;
    description?: string;
    type: string;
    estimate: number;
    price: number;
    billingPeriod?: 'oneTime' | 'monthly' | 'quarterly' | 'annual';
    free: boolean;
    removable: boolean;
    quantityMode?: boolean;
    quantity?: number;
    quantityMin?: number;
    quantityMax?: number;
    quantityEditable?: boolean;
    lineAmount?: number;
    excluded: boolean;
};

export type PublicQuoteData = {
    slug: number;
    name: string;
    status: string;
    price: number;
    monthlyPrice?: number;
    quarterlyPrice?: number;
    annualPrice?: number;
    projectName: string;
    client?: {
        company?: string;
        firstname?: string;
        lastname?: string;
    } | null;
    items: PublicQuoteItem[];
    canDecide: boolean;
    hasRemovableItems: boolean;
    hasQuantityEditableItems?: boolean;
    hasRecurringFees?: boolean;
    hasAnnualFees?: boolean;
    partialAcceptance?: boolean;
    introBody?: string;
    conditionsBody?: string;
    signature?: {
        signedAt?: string;
        signerName?: string;
        signerRole?: string;
        signerCompany?: string;
        signatureSource?: string;
    } | null;
};

const apiBase = () => {
    const normalizedBaseUrl = Config.api_end_point.replace(/\/+$/, '');
    return normalizedBaseUrl.endsWith('/api/v1')
        ? normalizedBaseUrl
        : `${normalizedBaseUrl}/api/v1`;
};

class PublicQuotes {
    getByToken(token: string): Promise<PublicQuoteData> {
        return fetch(`${apiBase()}/quotes/public/${token}`, {
            method: 'GET',
            headers: { Accept: 'application/json' },
        })
            .then(response => {
                if (!response.ok)
                    throw new Error(`HTTP ${response.status}`);
                return response.json();
            })
            .then(json => {
                if (json.quote)
                    return json.quote as PublicQuoteData;
                throw new Error(json.err || 'Not found');
            });
    }

    accept(token: string, payload: {
        acceptedItemIds: string[];
        itemQuantities?: Record<string, number>;
        signerName: string;
        signerRole?: string;
        signerCompany?: string;
        signatureImage: string;
        signatureSource: 'draw' | 'upload';
    }): Promise<PublicQuoteData> {
        return fetch(`${apiBase()}/quotes/public/${token}/accept`, {
            method: 'POST',
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(payload),
        })
            .then(async response => {
                const json = await response.json().catch(() => ({}));
                if (!response.ok)
                    throw new Error(json.err || `HTTP ${response.status}`);
                return json.quote as PublicQuoteData;
            });
    }

    refuse(token: string): Promise<PublicQuoteData> {
        return fetch(`${apiBase()}/quotes/public/${token}/refuse`, {
            method: 'POST',
            headers: { Accept: 'application/json' },
        })
            .then(async response => {
                const json = await response.json().catch(() => ({}));
                if (!response.ok)
                    throw new Error(json.err || `HTTP ${response.status}`);
                return json.quote as PublicQuoteData;
            });
    }
}

const PublicQuotesApi = new PublicQuotes();
export default PublicQuotesApi;
